import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { getCategory, computeOpenStatus, whatsappLink, mapsLink, mapsEmbed, dayLabel } from "../lib/constants";
import StarRating from "../components/StarRating";
import FavoriteButton from "../components/FavoriteButton";
import ClaimBusinessDialog from "../components/ClaimBusinessDialog";
import StickyActionBar from "../components/StickyActionBar";
import OpenStatusBadge from "../components/OpenStatusBadge";
import FirstReviewCTA from "../components/FirstReviewCTA";
import { ArrowLeft, Phone, MessageCircle, MapPin, Star, BadgeCheck, Sparkles, Mail, Globe, Share2, Check, Clock, Building2 } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function BusinessDetailPage() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [showClaim, setShowClaim] = useState(false);
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${slug}`);
      setBiz(data);
      const r = await api.get(`/businesses/${data.id}/reviews`);
      setReviews(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("business detail fetch failed:", e?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500" data-testid="business-loading">Chargement…</div>;
  if (!biz) return <div className="min-h-screen flex items-center justify-center" data-testid="business-not-found">Entreprise introuvable.</div>;

  const cat = getCategory(biz.category_slug);
  const status = computeOpenStatus(biz.hours);

  const track = (type) => {
    api.post(`/businesses/${biz.id}/track`, { type }).catch(() => {});
    // Mirror to GA4 with the public event names from the spec
    const map = { call: "call_click", whatsapp: "whatsapp_click", share: "share_click" };
    if (map[type]) {
      trackEvent(map[type], { business_slug: biz.slug, business_id: biz.id });
    }
  };

  const cover = biz.cover_image_url
    ? (biz.cover_image_url.startsWith("http") ? biz.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${biz.cover_image_url}`)
    : null;
  const gallery = (biz.photos || []).filter((p) => !p.is_cover).map((p) => (
    p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`
  ));

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login", { state: { redirect: `/business/${slug}` } }); return; }
    setSubmitting(true);
    setSubmitMsg("");
    try {
      await api.post(`/businesses/${biz.id}/reviews`, { rating, body: reviewBody });
      setSubmitMsg("Avis soumis ! Il sera publié après modération.");
      setReviewBody("");
      setShowReviewForm(false);
    } catch (e) {
      setSubmitMsg(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const cityLabel = biz.city_slug ? biz.city_slug.charAt(0).toUpperCase() + biz.city_slug.slice(1) : "";

  return (
    <div className="bg-lokaso-sand min-h-screen has-mobile-actionbar" data-testid="business-detail-page">
      {/* HERO */}
      <section className="relative" style={{ background: `linear-gradient(135deg, ${cat.color}, ${cat.color}cc)` }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12 text-white">
          <Link to="/search" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm" data-testid="biz-back-link">
            <ArrowLeft className="w-4 h-4" /> Retour aux résultats
          </Link>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold uppercase tracking-wider">
              <span>{cat.emoji}</span> {cat.name}
            </span>
            {biz.is_featured && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-lokaso-gold text-lokaso-dark text-xs font-bold">
                <Sparkles className="w-3 h-3" /> Mis en avant
              </span>
            )}
            {biz.is_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-lokaso-green text-xs font-semibold">
                <BadgeCheck className="w-3 h-3" /> Vérifié
              </span>
            )}
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-tight" data-testid="biz-name">
            {biz.name}
          </h1>
          {biz.tagline && <p className="mt-2 text-white/85 text-lg max-w-2xl">{biz.tagline}</p>}

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-lokaso-gold fill-lokaso-gold" />
              <span className="font-bold text-white">{(biz.avg_rating || 0).toFixed(1)}</span>
              <span className="text-white/80">· {biz.review_count} avis</span>
            </div>
            <span className="text-white/40">•</span>
            <span className="font-semibold">{biz.price_range}</span>
            <span className="text-white/40">•</span>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {biz.neighborhood_name}, {cityLabel}
            </div>
          </div>

          <div className="mt-3" data-testid="biz-live-status">
            <OpenStatusBadge hours={biz.hours} variant="block" />
          </div>

          {/* ACTION BUTTONS */}
          <div className="mt-6 flex flex-wrap gap-2 items-center" data-testid="biz-actions">
            <a href={`tel:${biz.phone}`} onClick={() => track("call")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-lokaso-dark font-semibold hover:brightness-95" data-testid="biz-call-btn">
              <Phone className="w-4 h-4" /> Appeler
            </a>
            {biz.whatsapp && (
              <a href={whatsappLink(biz.whatsapp, biz.name)} onClick={() => track("whatsapp")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600" data-testid="biz-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {biz.address && (
              <a href={mapsLink(biz.address)} onClick={() => track("maps")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-black/30 text-white font-semibold hover:bg-black/50" data-testid="biz-map-btn">
                <MapPin className="w-4 h-4" /> Voir sur la carte
              </a>
            )}
            <button onClick={() => setShowReviewForm((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-lokaso-gold text-lokaso-dark font-bold hover:brightness-95" data-testid="biz-review-btn">
              <Star className="w-4 h-4" /> Laisser un avis
            </button>
            <FavoriteButton businessId={biz.id} className="!bg-white/95 hover:!bg-white !border-transparent" />
            <button
              onClick={() => { setShowClaim(true); trackEvent("claim_started", { business_slug: biz.slug, business_id: biz.id }); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/90 hover:bg-white text-lokaso-dark font-semibold"
              data-testid="biz-claim-btn"
            >
              <Building2 className="w-4 h-4" /> Réclamer cette fiche
            </button>
          </div>
        </div>
      </section>

      {showClaim && <ClaimBusinessDialog business={biz} onClose={() => setShowClaim(false)} />}

      {/* Photo gallery */}
      {(cover || gallery.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-6" data-testid="biz-gallery">
          <div className={`grid gap-3 ${gallery.length === 0 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"}`}>
            {cover && (
              <div className={`relative rounded-2xl overflow-hidden border border-stone-200 ${gallery.length > 0 ? "md:col-span-2 md:row-span-2 aspect-[16/9] md:aspect-auto" : "aspect-[21/9]"}`}>
                <img src={cover} alt={biz.name} className="w-full h-full object-cover" />
              </div>
            )}
            {gallery.slice(0, 4).map((url, i) => (
              <div key={`${url}-${i}`} className="rounded-2xl overflow-hidden border border-stone-200 aspect-[4/3]">
                <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* LEFT */}
        <main className="space-y-8">
          {/* About */}
          <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8" data-testid="biz-about">
            <h2 className="font-display text-2xl font-bold mb-3">À propos</h2>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">{biz.description || "—"}</p>

            {biz.tags && biz.tags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {biz.tags.map((t) => (
                  <span key={t} className="pill" style={{ background: "white" }}>{t}</span>
                ))}
              </div>
            )}
          </section>

          {/* Amenities */}
          {biz.amenities && biz.amenities.length > 0 && (
            <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8" data-testid="biz-amenities">
              <h2 className="font-display text-2xl font-bold mb-4">Services & équipements</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {biz.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm">
                    <span className="w-7 h-7 rounded-full bg-lokaso-green/10 text-lokaso-green inline-flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </span>
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Review form */}
          {showReviewForm && (
            <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8" data-testid="biz-review-form">
              <h2 className="font-display text-2xl font-bold mb-4">Laisser un avis</h2>
              {!user && <p className="text-sm text-stone-600 mb-3">Vous devez être connecté pour publier un avis.</p>}
              <form onSubmit={submitReview} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Note</label>
                  <div className="mt-1 flex gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} type="button" onClick={() => setRating(n)} className="p-1" data-testid={`review-star-${n}`}>
                        <Star className={`w-7 h-7 ${n <= rating ? "text-lokaso-gold fill-lokaso-gold" : "text-stone-300"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Votre avis</label>
                  <textarea
                    value={reviewBody}
                    onChange={(e) => setReviewBody(e.target.value)}
                    required
                    minLength={10}
                    placeholder="Partagez votre expérience..."
                    className="mt-1 w-full min-h-[120px] rounded-xl border border-stone-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-lokaso-green"
                    data-testid="review-body-input"
                  />
                </div>
                {submitMsg && <p className="text-sm text-lokaso-green">{submitMsg}</p>}
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="review-submit-btn">
                  {submitting ? "Envoi…" : "Soumettre mon avis"}
                </button>
              </form>
            </section>
          )}

          {/* Reviews list */}
          <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8" data-testid="biz-reviews">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">Avis ({reviews.length})</h2>
              <div className="flex items-center gap-2">
                <StarRating rating={biz.avg_rating} showNumber />
              </div>
            </div>
            {reviews.length === 0 ? (
              <FirstReviewCTA onClick={() => setShowReviewForm(true)} />
            ) : (
              <ul className="space-y-5">
                {reviews.map((r) => (
                  <li key={r.id} className="flex gap-4 border-b border-stone-100 pb-5 last:border-0 last:pb-0">
                    <div className="w-10 h-10 rounded-full bg-lokaso-cream text-lokaso-dark font-bold flex items-center justify-center shrink-0">
                      {(r.author_name || "?")[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-semibold">{r.author_name}</p>
                        <StarRating rating={r.rating} showNumber={false} />
                      </div>
                      <p className="text-stone-700 mt-1 whitespace-pre-line">{r.body}</p>
                      <p className="text-xs text-stone-400 mt-1">
                        {new Date(r.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-6">
          {/* Contact card */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="biz-contact-card">
            <h3 className="font-display text-lg font-bold mb-4">Coordonnées</h3>
            <ul className="space-y-3 text-sm">
              {biz.address && (
                <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-lokaso-green shrink-0" /><span>{biz.address}</span></li>
              )}
              {biz.phone && (
                <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-lokaso-green shrink-0" /><a href={`tel:${biz.phone}`} className="hover:underline">{biz.phone}</a></li>
              )}
              {biz.whatsapp && (
                <li className="flex items-start gap-2"><MessageCircle className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><a href={whatsappLink(biz.whatsapp, biz.name)} target="_blank" rel="noreferrer" className="hover:underline">{biz.whatsapp}</a></li>
              )}
              {biz.email && (
                <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-lokaso-green shrink-0" /><a href={`mailto:${biz.email}`} className="hover:underline">{biz.email}</a></li>
              )}
              {biz.website && (
                <li className="flex items-start gap-2"><Globe className="w-4 h-4 mt-0.5 text-lokaso-green shrink-0" /><a href={biz.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{biz.website}</a></li>
              )}
            </ul>
          </div>

          {/* Hours */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="biz-hours">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Clock className="w-4 h-4" /> Horaires</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${status.open ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {status.label}
              </span>
            </div>
            <ul className="text-sm space-y-1">
              {DAYS.map((d) => {
                const v = (biz.hours || {})[d];
                const isClosed = !v || v === "closed";
                return (
                  <li key={d} className="flex items-center justify-between">
                    <span className="text-stone-600">{dayLabel(d)}</span>
                    <span className={isClosed ? "text-rose-600 font-medium" : "font-medium"}>{isClosed ? "Fermé" : v}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Map */}
          {biz.address && (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="biz-map">
              <iframe
                title="Map"
                src={mapsEmbed(biz.address)}
                className="w-full h-56 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}

          {/* Share */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="biz-share">
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Share2 className="w-4 h-4" /> Partager</h3>
            <div className="flex gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(`Découvrez ${biz.name} sur Lokaso: ${window.location.href}`)}`} onClick={() => { track("share"); trackEvent("share_click", { business_slug: biz.slug, channel: "whatsapp" }); }} target="_blank" rel="noreferrer" className="flex-1 text-center text-sm px-3 py-2 rounded-xl bg-emerald-500 text-white hover:bg-emerald-600">WhatsApp</a>
              <a href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} onClick={() => { track("share"); trackEvent("share_click", { business_slug: biz.slug, channel: "facebook" }); }} target="_blank" rel="noreferrer" className="flex-1 text-center text-sm px-3 py-2 rounded-xl bg-[#1877F2] text-white hover:brightness-110">Facebook</a>
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); track("share"); trackEvent("share_click", { business_slug: biz.slug, channel: "copy" }); }} className="flex-1 text-center text-sm px-3 py-2 rounded-xl bg-stone-100 hover:bg-stone-200" data-testid="biz-share-copy">Copier</button>
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile sticky action bar */}
      <StickyActionBar business={biz} onTrack={track} />
    </div>
  );
}
